import { BodyEmailTemplate, EmailTemplateType } from '@numeo/types-core'

/** Driver-scoped keys (Box Truck). The ONE list: the variable picker, the preview and the compose all read it,
 *  so a new driver field cannot reach the picker while the compose still sends it as raw {{braces}}. Lives in
 *  load-hub, the compose's side, because Box Truck already depends on load-hub and not the other way round. */
export const DRIVER_TEMPLATE_VARIABLE_KEYS = [
    'driver_name',
    'driver_phone',
    'driver_equipment',
    'miles_out',
    'driver_dimensions',
    'driver_payload',
    'dock_height_ramps',
    'equipment_capabilities',
    'driver_location',
    'truck_number',
] as const

/** Which broker email is being composed. */
export type ComposeEmailType = 'offer' | 'book' | 'info'

export const TYPE_TO_TEMPLATE_TYPE: Record<ComposeEmailType, EmailTemplateType> = {
    offer: EmailTemplateType.BID,
    book: EmailTemplateType.BROKER,
    info: EmailTemplateType.INFO,
}

/** Stable identity for a saved template — legacy rows predate `_id`, so fall back to name|subject.
 *  Shared with Box Truck's Info template dropdown so its pick and the resolution here can't diverge. */
export const templateKeyOf = (t: BodyEmailTemplate): string => t._id || `${t.name ?? ''}|${t.subject ?? ''}`

interface TemplateHolder {
    emailTemplates?: { bodyTemplates?: BodyEmailTemplate[] }
}

interface MailboxHolder extends TemplateHolder {
    email?: string
    isPrimary?: boolean
}

/** The sender's saved templates: nylas-level when it actually HAS any, else user-level. The `?.length`
 *  check matters — an EMPTY nylas array is truthy, and a bare `||` chain would let it win and hide the
 *  user-level templates. ONE resolution shared by the compose and Box Truck's template dropdown, so
 *  what the dropdown lists is always what the compose can resolve. */
export const resolveBodyTemplates = (nylasEmail: TemplateHolder | null | undefined, user: TemplateHolder | null | undefined): BodyEmailTemplate[] =>
    nylasEmail?.emailTemplates?.bodyTemplates?.length ? nylasEmail.emailTemplates.bodyTemplates : (user?.emailTemplates?.bodyTemplates ?? [])

/** The mailbox a send goes from: the one chosen in the selector, else the primary, else the first. */
export const resolveSenderMailbox = <T extends MailboxHolder>(mailboxes: T[] | undefined, selectedEmail?: string): T | null => {
    if (!mailboxes?.length) return null
    if (selectedEmail) {
        const found = mailboxes.find((mailbox) => mailbox.email === selectedEmail)
        if (found) return found
    }
    return mailboxes.find((mailbox) => mailbox.isPrimary) || mailboxes[0]
}

/**
 * The saved template a compose will use for this type, or `null` when it has none to use and the bundled
 * default takes over.
 *
 * One function rather than one per caller. The Box Truck drawer has to know the ANSWER before it renders
 * the compose — it decides there whether to force the board's bundled card — and a second implementation
 * of "which template would the compose pick" is a disagreement waiting to happen: the drawer would show
 * one email and the send would carry another. Every rule lives here, including the order.
 */
export const resolveSavedTemplate = (
    user: (TemplateHolder & { nylasEmails?: MailboxHolder[] }) | null | undefined,
    options: { type: ComposeEmailType; selectedEmail?: string; selectedTemplateId?: string; overrideId?: string }
): BodyEmailTemplate | null => {
    const mailbox = resolveSenderMailbox(user?.nylasEmails, options.selectedEmail)
    const templates = resolveBodyTemplates(mailbox, user)
    if (templates.length === 0) return null

    // An explicit caller pick (Box Truck's Info template dropdown) beats every implicit resolution.
    if (options.overrideId) {
        const chosen = templates.find((template) => templateKeyOf(template) === options.overrideId)
        if (chosen?.body && chosen?.subject) return chosen
    }
    // The FIRST template of the type, and only when it can actually be sent: a half-written one falls
    // through rather than being searched past, which is what the seed-once path depends on.
    const typeMatched = templates.find((template) => template.type === TYPE_TO_TEMPLATE_TYPE[options.type])
    if (typeMatched?.body && typeMatched?.subject) return typeMatched

    if (options.selectedTemplateId) {
        const selected = templates.find((template) => template._id === options.selectedTemplateId)
        if (selected?.body && selected?.subject) return selected
    }
    return null
}
